import React from 'react';
import { Button, Card, Divider, Flex, Table, Tag, Typography, theme } from 'antd';
import {
  BookOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  DashboardOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  ExportOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

import { RenderProofResponse } from '../api/RenderProofResponse';
import { SCREEN_READER_ONLY } from '../ScreenReaderOnly';
import {
  cacheStateHint,
  cacheStateLabel,
  isSuccessfulRenderStatus,
  renderStatusLabel,
} from '../RenderProofFormatting';
import {
  buildRenderProofComparison,
  ComparisonRow,
} from '../RenderProofComparison';

const { Title, Paragraph, Text } = Typography;

// FIXME: no exact token — Figma centres the content in a ~960px column.
const CONTENT_MAX_WIDTH = 960;
// FIXME: no exact token — Figma's success badge is a ~64px light-green circle.
const SUCCESS_BADGE_SIZE = 64;
// FIXME: no exact token — leading summary icons sit in a ~40px light-green circle.
const SUMMARY_ICON_SIZE = 40;
// FIXME: no exact token — constrains the footer copy so it wraps onto two lines like Figma.
const FOOTER_TEXT_MAX_WIDTH = 260;

const STATUS_CODE_DOCS_URL = 'https://docs.prerender.io/docs/status-codes';

/** The screen is either celebrating a render or flagging one that needs a look. */
type ScreenTone = 'success' | 'warning';

type SummaryItemProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  tone: ScreenTone;
  /** Draws the hint in the tone's colour rather than as secondary text. */
  emphasiseHint?: boolean;
};

function SummaryItem({
  icon,
  label,
  value,
  hint,
  tone,
  emphasiseHint = false,
}: SummaryItemProps) {
  const { token } = theme.useToken();
  const background =
    tone === 'success' ? token.colorSuccessBg : token.colorWarningBg;
  const foreground =
    tone === 'success' ? token.colorSuccess : token.colorWarning;

  return (
    <Flex gap={token.marginSM} align="flex-start">
      <Flex
        align="center"
        justify="center"
        style={{
          width: SUMMARY_ICON_SIZE,
          height: SUMMARY_ICON_SIZE,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: background,
          color: foreground,
          fontSize: token.fontSizeLG,
        }}
      >
        {icon}
      </Flex>
      <Flex vertical gap={token.marginXXS}>
        <Text type="secondary">{label}</Text>
        <Text strong>{value}</Text>
        <Text
          type={emphasiseHint ? undefined : 'secondary'}
          style={{
            fontSize: token.fontSizeSM,
            color: emphasiseHint ? foreground : undefined,
          }}
        >
          {hint}
        </Text>
      </Flex>
    </Flex>
  );
}

type RenderProofScreenProps = {
  proof: RenderProofResponse;
  onAddSitemap: () => void;
  onGoToDashboard: () => void;
  /** Called when the status-code documentation is opened. */
  onTroubleshootStatusCode?: () => void;
  /**
   * Wording for the closing action. The dashboard shows this proof in a modal
   * over the dashboard itself, where "Go to dashboard" describes nothing the
   * button does.
   */
  goToDashboardLabel?: string;
};

function RenderProofScreen({
  proof,
  onAddSitemap,
  onGoToDashboard,
  onTroubleshootStatusCode,
  goToDashboardLabel = 'Go to dashboard',
}: RenderProofScreenProps) {
  const { token } = theme.useToken();
  const renderStatus = renderStatusLabel(proof.renderStatusCode);
  const comparison = buildRenderProofComparison(proof);

  const renderSucceeded = isSuccessfulRenderStatus(proof.renderStatusCode);
  const tone: ScreenTone = renderSucceeded ? 'success' : 'warning';

  // The cache row is reported as it stands. A status class does not decide
  // cacheability: 200 always caches, 301 caches when the account asked for it,
  // and everything else never does — so a non-2xx proof that *is* cached is a
  // configured redirect being served to crawlers, and saying otherwise would be
  // the same false claim in the other direction.
  const { cacheState } = proof;

  // A status the crawler would not have cached is worth naming outright rather
  // than dressing as a success — the integration still works, the page needs a
  // look. The comparison table goes with it: an error page can carry more links
  // than the origin did, and calling that an improvement is the contradiction
  // this state exists to remove.
  const heading = renderSucceeded
    ? 'Your first page has been rendered'
    : `Prerender reached your page, but it returned ${renderStatus.value}`;
  // The uncached wording is product's. It cannot be used when the response *is*
  // cached — a configured 301 is cached and served, so telling the customer
  // crawlers cannot receive it contradicts the cache row directly beneath it.
  // That is the only non-2xx state reachable today, so it is the one that has to
  // read correctly.
  const subtitle = renderSucceeded
    ? 'Crawlers can now receive a fully rendered version of your page, ensuring your content is accessible'
    : cacheState === 'cached'
      ? 'Your integration is connected. Crawlers are being served this response, which may not be the content you expect.'
      : 'Your integration is connected, but this page may need attention before crawlers can receive a cached rendered version.';
  const showComparison = renderSucceeded && comparison.isVisible;

  const beforeColumn = {
    title: 'Before Prerender',
    dataIndex: 'before',
    key: 'before',
    align: 'right' as const,
  };

  const improvementColumn = {
    title: 'Improvement',
    dataIndex: 'improvement',
    key: 'improvement',
    align: 'center' as const,
    // A gain is green; anything else — a metric that did not move, or one with
    // nothing to compare against — gets a neutral tag. Anything *worse* never
    // reaches the table at all: buildRenderProofComparison drops the row.
    render: (improvement: string, row: ComparisonRow) =>
      row.tone === 'gain' ? (
        <Tag color="green">{improvement}</Tag>
      ) : (
        <Tag>{improvement}</Tag>
      ),
  };

  const columns = [
    {
      // Named for screen readers only; the design leaves this header blank.
      title: <span style={SCREEN_READER_ONLY}>Metric</span>,
      dataIndex: 'metric',
      key: 'metric',
    },
    ...(comparison.showBeforeColumn ? [beforeColumn] : []),
    {
      title: 'With Prerender',
      dataIndex: 'after',
      key: 'after',
      align: 'right' as const,
      render: (after: string) => (
        <Text strong style={{ color: token.colorSuccessText }}>
          {after}
        </Text>
      ),
    },
    ...(comparison.showBeforeColumn ? [improvementColumn] : []),
  ];

  return (
    <Flex vertical align="center" gap="large" flex={1} style={{ width: '100%' }}>
      <Flex
        align="center"
        justify="center"
        style={{
          width: SUCCESS_BADGE_SIZE,
          height: SUCCESS_BADGE_SIZE,
          borderRadius: '50%',
          backgroundColor:
            tone === 'success' ? token.colorSuccessBg : token.colorWarningBg,
        }}
      >
        {renderSucceeded ? (
          <CheckCircleFilled
            style={{
              fontSize: token.fontSizeHeading2,
              color: token.colorSuccess,
            }}
          />
        ) : (
          <ExclamationCircleFilled
            style={{
              fontSize: token.fontSizeHeading2,
              color: token.colorWarning,
            }}
          />
        )}
      </Flex>

      <Flex vertical align="center" gap={token.marginXXS}>
        {/* Stays an h1 for the document outline; Figma sizes it as a heading 3,
            which also keeps the longest status headline on one line. */}
        <Title
          level={1}
          style={{
            marginBottom: 0,
            textAlign: 'center',
            fontSize: token.fontSizeHeading3,
          }}
        >
          {heading}
        </Title>
        <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 0 }}>
          {subtitle}
        </Paragraph>
      </Flex>

      <Card
        style={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Flex gap="large" wrap justify="space-around">
          <SummaryItem
            icon={<GlobalOutlined />}
            label="Page"
            value={proof.url}
            hint="Homepage"
            tone={tone}
          />
          <SummaryItem
            icon={
              renderSucceeded ? (
                <CheckCircleOutlined />
              ) : (
                <ExclamationCircleOutlined />
              )
            }
            label="Status"
            value={renderStatus.value}
            hint={renderStatus.hint}
            tone={tone}
            emphasiseHint={!renderSucceeded}
          />
          <SummaryItem
            icon={<DashboardOutlined />}
            label="Cache status"
            value={cacheStateLabel(cacheState)}
            hint={
              !renderSucceeded && cacheState === 'not_cached'
                ? 'Pages with this status are not cached'
                : cacheStateHint(cacheState)
            }
            tone={tone}
          />
        </Flex>

        {showComparison && (
          <>
            <Divider />

            <Paragraph strong style={{ textAlign: 'center' }}>
              {comparison.showBeforeColumn
                ? 'See what crawlers saw before and after Prerender.'
                : 'See what Prerender rendered for crawlers.'}
            </Paragraph>
            {comparison.showBeforeColumn && (
              <Paragraph type="secondary" style={{ textAlign: 'center' }}>
                Before Prerender is your homepage as crawlers read it without
                rendering. With Prerender is the same page after Prerender
                rendered it.
              </Paragraph>
            )}

            <Table
              columns={columns}
              dataSource={comparison.rows}
              pagination={false}
              size="middle"
              style={{ width: '100%' }}
            />
          </>
        )}
      </Card>

      <Flex
        align="center"
        justify="flex-end"
        gap="middle"
        wrap
        style={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH }}
      >
        {renderSucceeded && (
          <Text
            type="secondary"
            style={{
              display: 'inline-block',
              maxWidth: FOOTER_TEXT_MAX_WIDTH,
              textAlign: 'right',
            }}
          >
            Submit your sitemap to help crawlers discover your important pages.
          </Text>
        )}
        <Flex gap="middle">
          {renderSucceeded ? (
            <Button type="primary" onClick={onAddSitemap}>
              Add sitemap
            </Button>
          ) : (
            <Button
              type="primary"
              href={STATUS_CODE_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onTroubleshootStatusCode}
              icon={<BookOutlined />}
            >
              Troubleshoot status code <ExportOutlined />
            </Button>
          )}
          <Button onClick={onGoToDashboard}>{goToDashboardLabel}</Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default RenderProofScreen;
